"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Separator } from "./ui/separator";
import { addPayAddress, updatePayAddress } from "../services/settingsService";
import { useAuth } from "../context/AuthContext";
import { Loader2, Info, Plus, ChevronDown, Check, X } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { SecuredFooter } from "./ui/secured-footer";
import AddressNetworkSelector from "./AddressNetworkSelector";
import { createWalletAddress } from "../services/settingsService";

const AddPayID = ({ setPageNo, PayCount, action, updateUser, setRefresh }) => {
  const { toast } = useToast();
  const { userProfile } = useAuth();
  const authId = userProfile.authId;

  // Tab state: "payid" or "crypto"
  const [activeTab, setActiveTab] = useState("payid");

  // Contact type
  const [contactType, setContactType] = useState("individual");

  // PayID fields
  const [name, setName] = useState("");
  const [lastname, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [payIds, setPayIds] = useState([{ id: 1, payId: "" }]);

  // Crypto fields (multiple addresses)
  const [addresses, setAddresses] = useState([
    { id: 1, selectedAsset: "", selectedNetwork: "", walletNo: "" },
  ]);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  // PayID validation: 1 capital letter followed by 6 digits (e.g., A123456)
  const validatePayId = (value) => {
    if (!value) return "";
    const payIdRegex = /^[A-Z][0-9]{6}$/;
    if (!payIdRegex.test(value)) {
      return "Invalid PayID format. Use 1 capital letter + 6 digits (e.g., A123456)";
    }
    return "";
  };

  const handlePayIdChange = (id, value) => {
    setPayIds((prev) =>
      prev.map((item) => (item.id === id ? { ...item, payId: value } : item))
    );
    const error = validatePayId(value);
    setErrors((prev) => ({ ...prev, [`payid_${id}`]: error }));
  };

  const handleAddAnotherPayId = () => {
    const newId = Math.max(...payIds.map((p) => p.id)) + 1;
    setPayIds([...payIds, { id: newId, payId: "" }]);
  };

  const handleRemovePayId = (id) => {
    if (payIds.length > 1) {
      setPayIds((prev) => prev.filter((item) => item.id !== id));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`payid_${id}`];
        return newErrors;
      });
    }
  };

  // Crypto address handlers
  const updateAddress = (id, field, value) => {
    setAddresses((prev) =>
      prev.map((addr) => (addr.id === id ? { ...addr, [field]: value } : addr))
    );
  };

  const handleAddAnotherAddress = () => {
    const newId = Math.max(...addresses.map((a) => a.id)) + 1;
    setAddresses([
      ...addresses,
      { id: newId, selectedAsset: "", selectedNetwork: "", walletNo: "" },
    ]);
  };

  const handleRemoveAddress = (id) => {
    if (addresses.length > 1) {
      setAddresses((prev) => prev.filter((addr) => addr.id !== id));
    }
  };

  // Check if form is filled
  const FilledCheck = useCallback(() => {
    const nameFieldsFilled =
      contactType === "corporate"
        ? businessName !== ""
        : name !== "" && lastname !== "";

    if (activeTab === "payid") {
      const allPayIdsFilled = payIds.every((item) => item.payId !== "");
      const noErrors = payIds.every((item) => !errors[`payid_${item.id}`]);
      setIsFilled(nameFieldsFilled && allPayIdsFilled && noErrors);
    } else {
      const allAddressesFilled = addresses.every(
        (addr) =>
          addr.walletNo !== "" &&
          addr.selectedAsset &&
          addr.selectedAsset.assetId &&
          addr.selectedNetwork &&
          (addr.selectedNetwork._id || addr.selectedNetwork.id)
      );
      setIsFilled(nameFieldsFilled && allAddressesFilled);
    }
  }, [
    name,
    lastname,
    businessName,
    contactType,
    payIds,
    addresses,
    errors,
    activeTab,
  ]);

  // API calls
  async function addPayAddressCall() {
    setIsLoading(true);

    try {
      for (const item of payIds) {
        const address = {
          name: contactType === "corporate" ? businessName : name,
          lastname: contactType === "corporate" ? businessName : lastname,
          payId: item.payId,
          category: contactType === "individual" ? "Individual" : "Corporate",
        };

        const apiCall = await addPayAddress(address, authId);
        if (apiCall.status !== 200 && apiCall.status !== 201) {
          throw new Error("Failed to add PayID");
        }
      }

      setRefresh(true);
      setIsLoading(false);
      setShowSuccess(true);
    } catch (err) {
      setIsLoading(false);
      toast({
        description:
          err.response?.data?.message || err.message || "Failed to add PayID",
        variant: "destructive",
      });
    }
  }

  async function addCryptoAddressCall() {
    setIsLoading(true);

    try {
      for (const addr of addresses) {
        const categoryValue =
          contactType === "individual" ? "Individual" : "Corporate";
        const address = {
          firstName: contactType === "corporate" ? businessName : name,
          lastName: contactType === "corporate" ? businessName : lastname,
          addressName:
            contactType === "corporate" ? businessName : `${name} ${lastname}`,
          walletAddress: addr.walletNo,
          asset: addr.selectedAsset.assetId,
          assetName: addr.selectedAsset.assetName || addr.selectedAsset.asset,
          chain: addr.selectedNetwork._id || addr.selectedNetwork.id,
          chainName: addr.selectedNetwork.chain,
          walletType: "80eight",
          category: categoryValue,
        };

        const apiCall = await createWalletAddress(address);
        if (!apiCall || (apiCall.status !== 200 && apiCall.status !== 201)) {
          throw new Error("Failed to create address");
        }
      }

      setRefresh(true);
      setIsLoading(false);
      setShowSuccess(true);
    } catch (err) {
      setIsLoading(false);
      toast({
        description:
          err.response?.data?.message || err.message || "Failed to add address",
        variant: "destructive",
      });
    }
  }

  async function updatePayAddressCall() {
    setIsLoading(true);
    const address = {
      name: contactType === "corporate" ? businessName : name,
      lastname: contactType === "corporate" ? businessName : lastname,
      payId: payIds[0].payId,
      category: contactType === "individual" ? "Individual" : "Corporate",
    };

    try {
      const apiCall = await updatePayAddress(address, updateUser?._id, authId);
      if (apiCall.status === 200) {
        setRefresh(true);
        setIsLoading(false);
        setShowSuccess(true);
      }
    } catch (err) {
      setIsLoading(false);
      toast({
        description: err.response?.data?.message || "Failed to update PayID",
        variant: "destructive",
      });
    }
  }

  // Load update data
  useEffect(() => {
    if (updateUser != null && name === "" && lastname === "") {
      setName(updateUser.name || "");
      setLastName(updateUser.lastname || "");
      if (updateUser.payId) {
        setPayIds([{ id: 1, payId: updateUser.payId }]);
      }
      if (updateUser.category) {
        setContactType(
          updateUser.category === "Corporate" ? "corporate" : "individual"
        );
      }
    }
  }, [updateUser, name, lastname]);

  // Check filled on change
  useEffect(() => {
    FilledCheck();
  }, [FilledCheck]);

  const handleDone = () => {
    setPageNo(0);
  };

  const handleSave = () => {
    if (action === "update") {
      updatePayAddressCall();
    } else if (activeTab === "payid") {
      addPayAddressCall();
    } else {
      addCryptoAddressCall();
    }
  };

  // Success Screen
  if (showSuccess) {
    return (
      <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-visible">
        <CardContent className="p-0">
          <div className="flex flex-col items-center justify-center py-16 px-8">
            <div className="mb-8">
              <div className="w-24 h-24 flex items-center justify-center">
                <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-[#EB3366] flex items-center justify-center relative">
                  <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-[#57D4E9]" />
                  <div className="absolute -bottom-2 -left-3 text-[#EB3366]">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="currentColor"
                    >
                      <path d="M6 0L7.5 4.5L12 6L7.5 7.5L6 12L4.5 7.5L0 6L4.5 4.5L6 0Z" />
                    </svg>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-[#EB3366] flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-[24px] font-semibold text-[#33125F] mb-2">
              {activeTab === "payid" ? "PayID Added" : "Address Added"}
            </h2>
            <p className="text-[14px] text-[#6D6584] text-center mb-8">
              Your contact has been saved successfully.
            </p>
            <Button
              onClick={handleDone}
              className="h-[48px] px-16 rounded-full text-[14px] font-semibold text-white"
              style={{
                background:
                  "linear-gradient(69.77deg, #57249C 10.66%, #EB3366 113.47%)",
              }}
            >
              Done
            </Button>
          </div>
          <SecuredFooter />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-visible">
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-8 pb-0">
          <h2 className="text-[20px] font-semibold text-[#33125F] mb-6">
            {action === "update" ? "Edit contact" : "Add a new contact"}
          </h2>

          {/* Blue Info Banner */}
          <div className="bg-[#D9FCFF] rounded-2xl px-6 py-4 flex items-center gap-3 mb-6">
            <Info className="w-5 h-5 text-[#0891B2] flex-shrink-0" />
            <p className="text-[14px] text-[#0891B2]">
              You can add a PayID or a traditional crypto address.
            </p>
          </div>

          {/* PayID / Crypto Toggle */}
          <div className="w-full h-[64px] p-2 bg-transparent border border-[#EBE9EF] rounded-full flex mb-6">
            <button
              onClick={() => setActiveTab("payid")}
              className={`flex-1 h-[48px] rounded-full lg:text-lg text-[14px] font-semibold transition-colors ${
                activeTab === "payid"
                  ? "bg-[#33125F] text-white"
                  : "text-[#6D6584]"
              }`}
            >
              PayID
            </button>
            <button
              onClick={() => setActiveTab("crypto")}
              className={`flex-1 h-[48px] rounded-full lg:text-lg text-[14px] font-semibold transition-colors ${
                activeTab === "crypto"
                  ? "bg-[#33125F] text-white"
                  : "text-[#6D6584]"
              }`}
            >
              Crypto
            </button>
          </div>
        </div>

        {/* Form Fields */}
        <div className="px-8 pb-6">
          {/* Name Fields */}
          {contactType === "corporate" ? (
            <div className="mb-6">
              <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                Business name
              </p>
              <input
                type="text"
                placeholder="Business name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="h-[56px] border border-[#CECBD7] rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Name
                </p>
                <input
                  type="text"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-[56px] border border-[#CECBD7] rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition"
                />
              </div>
              <div>
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Surname
                </p>
                <input
                  type="text"
                  placeholder="Surname"
                  value={lastname}
                  onChange={(e) => setLastName(e.target.value)}
                  className="h-[56px] border border-[#CECBD7] rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition"
                />
              </div>
            </div>
          )}

          {/* Type Dropdown */}
          <div className="mb-6 relative z-20">
            <p className="text-[14px] text-[#6D6584] font-normal mb-2">Type</p>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                className={`h-[56px] rounded-2xl w-full px-6 text-[16px] text-left flex items-center justify-between transition bg-white ${
                  showTypeDropdown
                    ? "border-2 border-[#57249C]"
                    : "border border-[#CECBD7]"
                }`}
              >
                <span className="text-[#423E51]">
                  {contactType === "individual" ? "Individual" : "Corporate"}
                </span>
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${
                    showTypeDropdown
                      ? "rotate-180 text-[#57249C]"
                      : "text-[#6D6584]"
                  }`}
                />
              </button>
              {showTypeDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#CECBD7] rounded-2xl shadow-lg z-50 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setContactType("individual");
                      setShowTypeDropdown(false);
                    }}
                    className="w-full px-6 py-4 text-left text-[16px] text-[#423E51] hover:bg-[#F5F3F8] transition-colors flex items-center justify-between"
                  >
                    Individual
                    {contactType === "individual" && (
                      <Check className="w-5 h-5 text-[#57249C]" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setContactType("corporate");
                      setShowTypeDropdown(false);
                    }}
                    className="w-full px-6 py-4 text-left text-[16px] text-[#423E51] hover:bg-[#F5F3F8] transition-colors flex items-center justify-between"
                  >
                    Corporate
                    {contactType === "corporate" && (
                      <Check className="w-5 h-5 text-[#57249C]" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* PayID Tab Content */}
          {activeTab === "payid" && (
            <>
              {/* PayID Info */}
              <div className="bg-[#EBF5FF] rounded-xl px-4 py-3 flex items-start gap-2 mb-4">
                <Info className="w-4 h-4 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                <p className="text-[13px] text-[#3B82F6]">
                  PayID format: 1 capital letter + 6 digits (e.g., A123456)
                </p>
              </div>

              {/* PayID Inputs */}
              {payIds.map((item, index) => (
                <div key={item.id} className="mb-4">
                  {payIds.length > 1 && (
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[14px] font-semibold text-[#33125F]">
                        PayID {index + 1}
                      </p>
                      <button
                        onClick={() => handleRemovePayId(item.id)}
                        className="text-[#6D6584] hover:text-[#EB3366] transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                  <div>
                    <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                      PayID
                    </p>
                    <input
                      type="text"
                      placeholder="e.g. A123456"
                      value={item.payId}
                      onChange={(e) =>
                        handlePayIdChange(item.id, e.target.value.toUpperCase())
                      }
                      className={`h-[56px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#CECBD7] focus:outline-none focus:ring-2 transition ${
                        errors[`payid_${item.id}`]
                          ? "border-[#EF4444] focus:ring-[#EF4444]"
                          : "border-[#CECBD7] focus:ring-[#57249C]"
                      }`}
                    />
                    {errors[`payid_${item.id}`] && (
                      <p className="text-[#EF4444] text-[13px] mt-2">
                        {errors[`payid_${item.id}`]}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* Add Another PayID */}
              {action !== "update" && (
                <button
                  onClick={handleAddAnotherPayId}
                  className="flex items-center gap-2 text-[16px] text-[#060606] font-semibold py-4 hover:opacity-80 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  Add another PayID
                </button>
              )}
            </>
          )}

          {/* Crypto Tab Content */}
          {activeTab === "crypto" && (
            <>
              {addresses.map((addr, index) => (
                <div key={addr.id} className="mb-6">
                  {addresses.length > 1 && (
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-[16px] font-semibold text-[#33125F]">
                        Address {index + 1}
                      </p>
                      <button
                        onClick={() => handleRemoveAddress(addr.id)}
                        className="text-[#6D6584] hover:text-[#EB3366] transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-1">
                      <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                        Select asset
                      </p>
                      <AddressNetworkSelector
                        setSelectedAsset={(asset) =>
                          updateAddress(addr.id, "selectedAsset", asset)
                        }
                        setSelectedNetwork={(network) =>
                          updateAddress(addr.id, "selectedNetwork", network)
                        }
                        selectedAsset={addr.selectedAsset}
                        selectedNetwork={addr.selectedNetwork}
                        action={action}
                        FilledCheck={FilledCheck}
                      />
                    </div>
                    <div className="col-span-2">
                      <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                        Wallet address
                      </p>
                      <input
                        type="text"
                        placeholder="Enter wallet address"
                        value={addr.walletNo}
                        onChange={(e) =>
                          updateAddress(addr.id, "walletNo", e.target.value)
                        }
                        className="h-[88px] border border-[#CECBD7] rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition"
                      />
                    </div>
                  </div>

                  {index < addresses.length - 1 && (
                    <Separator className="bg-[#EBE9EF] mt-6" />
                  )}
                </div>
              ))}

              {/* Add Another Address */}
              {action !== "update" && (
                <button
                  onClick={handleAddAnotherAddress}
                  className="flex items-center gap-2 text-[16px] text-[#060606] font-semibold py-4 hover:opacity-80 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  Add another address
                </button>
              )}
            </>
          )}
        </div>

        <Separator className="bg-[#EBE9EF]" />

        {/* Action Buttons */}
        <div className="p-8 flex gap-4">
          <Button
            onClick={() => setPageNo(0)}
            className="flex-1 h-[48px] bg-white border border-[#EBE9EF] text-[#6D6584] text-[14px] font-semibold rounded-full hover:bg-[#F5F3F8] transition-colors"
          >
            Cancel
          </Button>
          <Button
            disabled={!isFilled || isLoading}
            onClick={handleSave}
            className="flex-1 h-[48px] rounded-full text-[14px] font-semibold text-white disabled:opacity-50"
            style={{
              background:
                "linear-gradient(69.77deg, #57249C 10.66%, #EB3366 113.47%)",
            }}
          >
            {isLoading ? (
              <Loader2 className="animate-spin text-white" />
            ) : activeTab === "payid" ? (
              "Save PayID"
            ) : (
              "Save Address"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AddPayID;
